/**
 * `useResolvedCampaignId` — shared slug-to-ObjectID resolver hook for
 * the campaign-detail surface. Single source of truth across the admin
 * and brand apps; each app's local wrapper just reads its own route
 * params and forwards them here.
 *
 * Pattern (mirrors Linear, Stripe, GitHub):
 *
 *   • Browser URL holds a globally-unique campaign slug (e.g.
 *     `gemini-mere-life-ka-ai-partner-euM`).
 *   • One call to `GET /api/v1/campaigns/by-slug/{slug}` on page mount
 *     resolves slug → full campaign payload. Cached forever
 *     (staleTime: Infinity) — the ObjectID is permanent for a given
 *     slug.
 *   • Every subsequent API call uses the resolved ObjectID. The BE
 *     endpoints (`/campaigns/{id}/roster/*`, `/kpis`, etc.) are strict
 *     ObjectID-only.
 *
 * Optional `rawId` short-circuit: when the URL carries an ObjectID
 * directly (admin internal links like `/dashboard/campaigns/{id}`),
 * skip the resolver call — no slug to translate.
 *
 * Row scoping is enforced by the BE handler via JWT — brand_owner sees
 * only own brand's campaigns (404 on cross-brand, never 200 with nulled
 * fields).
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient, isNotFoundError } from '@discovr/common';

interface CampaignBrief {
  id: string;
  [key: string]: unknown;
}

async function resolveBySlug(slug: string): Promise<CampaignBrief> {
  const res = await apiClient.get<{ data: CampaignBrief }>(
    `/campaigns/by-slug/${encodeURIComponent(slug)}`,
  );
  return res.data.data;
}

export interface UseResolvedCampaignIdInput {
  /**
   * Campaign slug from the URL (the primary identifier the user sees).
   * Required for the slug-based path; the hook fires a resolver call
   * when this is set.
   */
  campaignSlug?: string;
  /**
   * Optional ObjectID short-circuit. When present (and `campaignSlug`
   * is not), the hook returns this id synchronously with no network
   * call — used by admin's fallback `/campaigns/{id}` URLs.
   */
  rawId?: string;
}

export interface UseResolvedCampaignIdResult {
  /** Resolved ObjectID. Empty string until the resolver settles. */
  id: string;
  /** True while the resolver request is in flight. */
  loading: boolean;
  /** Human-readable error message if resolution failed; null otherwise. */
  error: string | null;
  /**
   * True when resolution failed with HTTP 404 — the campaign doesn't exist OR
   * the signed-in user has no access (the BE returns one privacy-preserving
   * 404 for both). Callers render an explicit "not available" page rather than
   * an empty campaign shell.
   */
  isNotFound: boolean;
}

export function useResolvedCampaignId(
  input: UseResolvedCampaignIdInput,
): UseResolvedCampaignIdResult {
  const { campaignSlug, rawId } = input;

  // Short-circuit: raw ObjectID in URL (admin fallback path). No
  // resolver call needed — the id is already in the URL.
  const isShortUrl = !!rawId && !campaignSlug;

  const query = useQuery({
    queryKey: ['campaign-by-slug', campaignSlug],
    queryFn: () => resolveBySlug(campaignSlug!),
    enabled: !isShortUrl && !!campaignSlug,
    // The (slug → ObjectID) mapping is permanent; once resolved, never
    // refetch. gcTime caps in-memory cache at 1h to avoid leaks across
    // session lifetimes.
    staleTime: Infinity,
    gcTime: 60 * 60_000,
  });

  if (isShortUrl) {
    return { id: rawId!, loading: false, error: null, isNotFound: false };
  }

  if (!campaignSlug) {
    return {
      id: '',
      loading: false,
      error: 'Campaign slug missing from URL',
      isNotFound: false,
    };
  }

  return {
    id: query.data?.id ?? '',
    loading: query.isLoading,
    error: query.isError
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to resolve campaign'
      : null,
    isNotFound: query.isError && isNotFoundError(query.error),
  };
}
